import { Accordion } from '@whistler-ui/k2/components/Accordion';
import { useClassHelper } from '@whistler-ui/k2/hooks';
import { useTranslation } from 'react-i18next';

import { TEST_STATUSES } from '../../../constants';
import { GRAPH_LABELS } from '../../../constants/graphConstants';
import { useThemeStore } from '../../../store/theme';
import { ApiTestExecution, ILogsError } from '../../../types';
import { TestConsolePane } from '../../TestConsolePane';

interface Props {
  useCase: ApiTestExecution;
  activeItem: string;
  setActiveItem: (label: string) => void;
  componentName: string;
  isWebsheetOpen: boolean;
  executionStatus: string;
  index: number;
}

export const ExecutionItemInfo = ({
  useCase,
  activeItem,
  setActiveItem,
  componentName,
  isWebsheetOpen,
  executionStatus,
  index,
}: Props) => {
  const c = useClassHelper(componentName);
  const { t } = useTranslation();
  const { colors } = useThemeStore();
  const TAG: string =
    GRAPH_LABELS[useCase.test_case_tag] ||
    GRAPH_LABELS[useCase.api_tag] ||
    useCase.api_tag;
  const isActive = TAG === activeItem;

  const noPointer =
    isWebsheetOpen || useCase.status === TEST_STATUSES.CREATED
      ? 'no-pointer'
      : '';
  const websheetCancelled =
    isWebsheetOpen &&
    [TEST_STATUSES.CANCELLED, TEST_STATUSES.FAILED].includes(
      executionStatus as 'Failed' | 'Cancelled',
    );

  const getLabelColor = () => {
    if (useCase.status === 'Failed' || websheetCancelled) {
      return colors.error;
    } else if (isWebsheetOpen) {
      return colors.test;
    } else {
      return colors.success;
    }
  };

  const getLabel = () => {
    if (useCase.status === 'Failed') {
      return 'Fail';
    }

    if (websheetCancelled) {
      return 'Cancelled';
    }

    if (isWebsheetOpen) {
      return 'Redirect';
    }

    return 'Pass';
  };

  return (
    <Accordion
      key={useCase.id}
      handleOpen={() => setActiveItem(isActive ? null : TAG)}
      isOpen={isActive}
      dataToggleTestId={`execution-item-info-accordion-${index}`}
    >
      <Accordion.Header>
        <div className={`${c('__sub-header')} ${noPointer}`}>
          <div className={c('__headers__label')}>
            <span>{t(TAG)}</span>
          </div>
          <div
            className={c('__sub-header__tag')}
            style={{
              color: getLabelColor(),
            }}
          >
            {t(getLabel())}
          </div>
        </div>
      </Accordion.Header>
      <Accordion.Content>
        {useCase?.invocations?.map?.(({ request, response, id, error }) => {
          return (
            <TestConsolePane
              id={id}
              key={id}
              request={request}
              response={response}
              error={error as ILogsError}
            />
          );
        })}
      </Accordion.Content>
    </Accordion>
  );
};
